import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
} from "react";
import { useAuth } from "../../auth/context/AuthContext";
import { db } from "../../../lib/firebase";
import { montarPayloadEdicao } from "../../../shared/utils/payloadEdicaoFirestore";
import {
  registrarEdicaoTrilha,
  registrarExclusaoTrilha,
} from "../../../shared/utils/trilhaAuditoria";
import {
  calcularCamposAlterados,
  CAMPOS_META_TRILHA,
} from "../../../shared/utils/trilhaAuditoriaDiff";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  deleteField,
} from "firebase/firestore";

interface ExameLab {
  id: string;
  pacienteId: string;
  data: string;
  editavel: boolean;
  tipoExame?: "pre" | "pos";
  meld?: number;
  childPugh?: string;
  encefalopatia?: string;
  ascite?: string;

  // CAMPOS PÓS-TX
  po?: number;
  temperatura?: number;
  peso?: number;
  dialise?: boolean;
  transfusao?: string;
  diurese?: number;

  // HEMOGRAMA
  hemoglobina?: number;
  hematocrito?: number;
  leucocitos?: number;
  neutrofilos?: number;
  eosinofilos?: number;
  linfocitos?: number;
  bastonetes?: number; // B342 (AI_150)
  plaquetas?: number;

  // COAGULAÇÃO
  tap?: number;
  inr?: number;
  ttpa?: number;
  fibrinogenio?: number;
  fatorV?: number;

  // FUNÇÃO HEPÁTICA
  bilirrubinaTotal?: number;
  bilirrubinaDireta?: number;
  bilirrubinaIndireta?: number;
  tgo?: number;
  tgp?: number;
  fosfataseAlcalina?: number;
  ggt?: number;
  albumina?: number;
  proteinasTotais?: number; // B342 (AI_150)
  globulina?: number; // B342 (AI_150)

  // FUNÇÃO RENAL
  creatinina?: number;
  ureia?: number;

  // ELETRÓLITOS
  sodio?: number;
  potassio?: number;
  magnesio?: number;
  calcio?: number;
  calcioIonico?: number; // B241 fase 2 (AI_109)
  fosforo?: number; // B342 (AI_150) — FOSF do folhão (≠ fosfatase alcalina)
  cloro?: number; // B342 (AI_150)

  // PÂNCREAS (B241 fase 2 — AI_109)
  amilase?: number;
  lipase?: number;

  // LIPÍDIOS
  colesterolTotal?: number;
  ldl?: number;
  hdl?: number;
  vldl?: number;
  triglicerides?: number;

  // TIREOIDE
  tsh?: number;
  t4Livre?: number;
  t3Total?: number; // B241 fase 2 (AI_109)

  // METABOLISMO/INFECÇÃO
  glicemia?: number;
  hemoglobinaGlicada?: number;
  frutosamina?: number;
  acidoUrico?: number; // B241 fase 2 (AI_109)
  pcr?: number;
  procalcitonina?: number;
  lactato?: number;
  // B342 (AI_150) — campos do folhão do pós-operatório que faltavam.
  ldh?: number;
  cpk?: number;
  ckMb?: number;
  troponina?: number;

  // GASOMETRIA
  gasometriaTipo?: "arterial" | "venosa";
  gasometriaPh?: number;
  gasometriaPco2?: number;
  gasometriaPo2?: number;
  gasometriaHco3?: number;
  gasometriaBe?: number;
  gasometriaSaturacao?: number;

  // IMUNOSSUPRESSÃO
  solumedrol?: number;
  prednisona?: number;
  tacrolimus?: number;
  nivelTacrolimus?: number;
  ciclosporina?: number;
  nivelCiclosporina?: number;
  mmf?: number;
  mms?: number;
  everolimus?: number;
  nivelEverolimus?: number;
  sirolimus?: number;
  nivelSirolimus?: number;
  // B241 (AI_108): rapamicina/nivelRapamicina removidos — sirolimo = rapamicina
  // (mesma droga); um par só evita lançamento duplicado/erro.
  azatioprina?: number;
  thymoglobulina?: number;

  registradoPor: string;
  registradoPorNome: string;
  editadoPor?: string;
  editadoPorNome?: string;
  dataEdicao?: string;
  criadoEm?: string;
}
interface ExamesContextData {
  exames: ExameLab[];
  loading: boolean;
  adicionarExame: (
    pacienteId: string,
    exame: Omit<
      ExameLab,
      "id" | "pacienteId" | "registradoPor" | "registradoPorNome"
    >
  ) => Promise<string>;
  editarExame: (id: string, exame: Partial<ExameLab>) => Promise<void>;
  removerExame: (id: string) => Promise<void>;
  listarExamesPorPaciente: (pacienteId: string) => ExameLab[];
  listarExamesPreTx: (pacienteId: string) => ExameLab[];
  listarExamesPosTx: (pacienteId: string) => ExameLab[];
  marcarExamesComoPreTx: (
    pacienteId: string,
    dataTransplante?: string
  ) => Promise<void>;
  carregarExames: () => Promise<void>;
  limparExamesFantasmas: () => Promise<number>;
}

const ExamesContext = createContext<ExamesContextData>({} as ExamesContextData);

export function ExamesProvider({ children }: { children: ReactNode }) {
  const { usuarioLogado } = useAuth();
  const [exames, setExames] = useState<ExameLab[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // 📥 CARREGAR EXAMES (tempo real)
  // ============================================
  const carregarExames = async () => {}; // mantido para compatibilidade

  useEffect(() => {
    if (!usuarioLogado) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log("🔍 Escutando exames em tempo real...");

    const unsubscribe = onSnapshot(
      collection(db, "exames"),
      (querySnapshot) => {
        const examesFormatados: ExameLab[] = querySnapshot.docs.map(
          (docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              data: data.data?.toDate?.()?.toISOString?.() || data.data,
              dataEdicao:
                data.dataEdicao?.toDate?.()?.toISOString?.() || data.dataEdicao,
              criadoEm:
                data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
            } as ExameLab;
          }
        );

        setExames(examesFormatados);
        setLoading(false);
        console.log(
          "✅ Exames atualizados (tempo real):",
          examesFormatados.length
        );
      },
      (error) => {
        console.error("❌ Erro no listener de exames:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // ➕ ADICIONAR EXAME
  // ============================================
  const adicionarExame = async (
    pacienteId: string,
    exameData: Omit<
      ExameLab,
      "id" | "pacienteId" | "registradoPor" | "registradoPorNome"
    >
  ): Promise<string> => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Adicionando exame...");

      // Remove campos undefined antes de salvar
      const dadosLimpos = Object.fromEntries(
        Object.entries(exameData).filter(([_, value]) => value !== undefined)
      );

      const docRef = await addDoc(collection(db, "exames"), {
        pacienteId,
        registradoPor: usuarioLogado.id,
        registradoPorNome: usuarioLogado.nome,
        ...dadosLimpos,
        // B241 (AI_109): criadoEm com Timestamp.now() (cliente), NÃO serverTimestamp().
        // Motivo: o serverTimestamp volta NULO no snapshot otimista (hasPendingWrites)
        // até o servidor resolver. A tabela usa criadoEm p/ liberar editar/excluir
        // (janela de 24h); com o valor nulo, o exame importado (que entra já salvo,
        // editavel:false) ficava sem o ícone de editar até recarregar a tela. Com
        // Timestamp.now() o valor existe já no 1º snapshot. A regra de criação não
        // exige server time; a imutabilidade do campo no update é preservada.
        criadoEm: Timestamp.now(),
      });

      console.log("✅ Exame adicionado com sucesso! ID:", docRef.id);

      // ✅ Retorna o ID gerado pelo Firebase
      return docRef.id;
    } catch (error) {
      console.error("❌ Erro ao adicionar exame:", error);
      throw error;
    }
  };

  // EDITAR EXAME
  const editarExame = async (
    id: string,
    exameAtualizado: Partial<ExameLab>
  ) => {
    if (!usuarioLogado) {
      throw new Error("Usuário não autenticado");
    }

    try {
      console.log("📝 Editando exame:", id);

      // FIX preventivo (AI_99): campos imutáveis do B81 fora do payload
      // (criadoEm volta convertido p/ string e a regra negaria o update).
      const CAMPOS_BLOQUEADOS = new Set([
        "id",
        "registradoPor",
        "registradoPorNome",
        "criadoEm",
        "pacienteId",
      ]);
      // 🐛 B338 (AI_149) — APAGAR um valor tem de APAGAR mesmo.
      // Antes, o payload era FILTRADO por `v !== undefined` (o Firestore rejeita
      // undefined) — só que a tela usa `undefined` justamente para dizer "o médico
      // limpou esta célula" (InputExame.salvarValor). Resultado: o campo saía do
      // payload, o updateDoc não mexia nele e o valor VELHO permanecia, sem erro
      // nenhum. Achado ao vivo por Leonardo (28/07): não conseguia apagar uma
      // albumina de 0,1 g/dL emitida errada pelo laboratório.
      // Correção: `undefined` vira `deleteField()` — a intenção é apagar, e é
      // assim que o Firestore apaga. Nenhum chamador passa undefined por acidente
      // (todos montam o objeto com valor explícito ou spread condicional).
      const dadosFirebase: any = montarPayloadEdicao(
        exameAtualizado as Record<string, unknown>,
        CAMPOS_BLOQUEADOS,
        deleteField
      );

      // Se está salvando (editavel = false), registra auditoria
      if (exameAtualizado.editavel === false) {
        dadosFirebase.editadoPor = usuarioLogado.id;
        dadosFirebase.editadoPorNome = usuarioLogado.nome;
        dadosFirebase.dataEdicao = serverTimestamp();
      }

      await updateDoc(doc(db, "exames", id), dadosFirebase);

      // B213 — trilha para REEDIÇÃO de exame já finalizado. A digitação de
      // exames é CÉLULA A CÉLULA (cada campo dispara um update próprio),
      // então cada salvamento de campo gera sua entrada. "Já finalizado" =
      // tem carimbo de edição (gravado no 1º salvar) ou não está em
      // rascunho — a digitação inicial do rascunho fica de fora (a criação
      // já aparece no Relatório de Atividades). `editavel` (estado de
      // fluxo) e meld/childPugh (derivados) ficam fora do diff.
      const atual = exames.find((ex) => ex.id === id);
      const jaFinalizado =
        !!atual && (atual.editadoPor != null || atual.editavel !== true);
      if (jaFinalizado) {
        await registrarEdicaoTrilha({
          colecao: "exames",
          docId: id,
          pacienteId: atual.pacienteId || "",
          rotulo: atual.data
            ? `Exames de ${new Date(atual.data).toLocaleDateString("pt-BR")}`
            : "Exames",
          usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
          camposAlterados: calcularCamposAlterados(
            exameAtualizado,
            atual,
            new Set([
              ...Array.from(CAMPOS_META_TRILHA),
              "editavel",
              "meld",
              "childPugh",
            ])
          ),
        });
      }

      console.log("✅ Exame editado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao editar exame:", error);
      throw error;
    }
  };

  // ============================================
  // 🗑️ REMOVER EXAME
  // ============================================
  const removerExame = async (id: string) => {
    if (!usuarioLogado) {
      throw new Error("Usuário não autenticado");
    }

    try {
      console.log("🗑️ Removendo exame:", id);

      // B213 — captura antes do delete; rascunho vazio (editavel) não
      // gera lápide, só exame finalizado (evita ruído de fantasmas).
      const atual = exames.find((ex) => ex.id === id);

      await deleteDoc(doc(db, "exames", id));

      if (atual && atual.editavel === false) {
        await registrarExclusaoTrilha({
          colecao: "exames",
          docId: id,
          pacienteId: atual.pacienteId || "",
          rotulo: atual.data
            ? `Exames de ${new Date(atual.data).toLocaleDateString("pt-BR")}`
            : "Exames",
          usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        });
      }

      console.log("✅ Exame removido com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao remover exame:", error);
      throw error;
    }
  };

  // ============================================
  // 📋 LISTAR EXAMES
  // ============================================
  const listarExamesPorPaciente = (pacienteId: string) => {
    return exames
      .filter((exame) => exame.pacienteId === pacienteId)
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
  };

  const listarExamesPreTx = (pacienteId: string) => {
    return exames
      .filter(
        (exame) => exame.pacienteId === pacienteId && exame.tipoExame === "pre"
      )
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
  };

  const listarExamesPosTx = (pacienteId: string) => {
    return exames
      .filter(
        (exame) =>
          exame.pacienteId === pacienteId &&
          (!exame.tipoExame || exame.tipoExame === "pos")
      )
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
  };

  // ============================================
  // 🏷️ MARCAR COMO PRÉ-TX
  // ============================================
  const marcarExamesComoPreTx = async (
    pacienteId: string,
    dataTransplante?: string
  ) => {
    try {
      const examesParaMarcar = exames.filter((exame) => {
        if (exame.pacienteId !== pacienteId || exame.tipoExame) return false;
        // AI_20: Só marcar como pré-TX exames com data ANTERIOR ao transplante
        if (dataTransplante && exame.data) {
          const normalizarParaISO = (d: string): string => {
            if (!d) return d;
            const partes = d.split("/");
            if (partes.length === 3 && partes[0].length <= 2) {
              return `${partes[2]}-${partes[1]}-${partes[0]}`;
            }
            return d;
          };
          const dataExame = new Date(normalizarParaISO(exame.data));
          const dataTx = new Date(normalizarParaISO(dataTransplante));
          if (dataExame >= dataTx) return false; // Exame pós-TX — não marcar
        }
        return true;
      });
      for (const exame of examesParaMarcar) {
        await updateDoc(doc(db, "exames", exame.id), {
          tipoExame: "pre",
          editavel: false,
        });
      }

      await carregarExames();
      console.log("✅ Exames marcados como pré-TX");
    } catch (error) {
      console.error("❌ Erro ao marcar exames:", error);
      throw error;
    }
  };

  // ============================================
  // 🧹 LIMPAR EXAMES FANTASMAS
  // ============================================
  const limparExamesFantasmas = async () => {
    try {
      console.log("🧹 Iniciando limpeza de exames fantasmas...");

      const querySnapshot = await getDocs(collection(db, "exames"));
      let removidos = 0;

      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();

        // Verificar se é um exame fantasma (editável e sem dados relevantes)
        const ehFantasma =
          data.editavel === true &&
          !data.hemoglobina &&
          !data.hematocrito &&
          !data.leucocitos &&
          !data.plaquetas &&
          !data.creatinina &&
          !data.bilirrubinaTotal;

        if (ehFantasma) {
          console.log("🗑️ Removendo exame fantasma:", docSnap.id);
          await deleteDoc(doc(db, "exames", docSnap.id));
          removidos++;
        }
      }

      console.log(
        `✅ Limpeza concluída! ${removidos} exames fantasmas removidos.`
      );
      await carregarExames();

      return removidos;
    } catch (error) {
      console.error("❌ Erro ao limpar exames fantasmas:", error);
      throw error;
    }
  };

  const contextValue = useMemo(
    () => ({
      exames,
      loading,
      adicionarExame,
      editarExame,
      removerExame,
      listarExamesPorPaciente,
      listarExamesPreTx,
      listarExamesPosTx,
      marcarExamesComoPreTx,
      carregarExames,
      limparExamesFantasmas,
    }),
    [exames, loading]
  );

  return (
    <ExamesContext.Provider value={contextValue}>
      {children}
    </ExamesContext.Provider>
  );
}

export function useExames() {
  const context = useContext(ExamesContext);
  if (!context) {
    throw new Error("useExames deve ser usado dentro de ExamesProvider");
  }
  return context;
}
